import React from 'react';
import { Share2, Check, Loader2, X } from 'lucide-react';
import { Cut, Label, ThButton } from './Cut';

// One tap on a category = that playlist starts, with sound. "Play all films" is the single primary action.
// While the main film already plays behind it, the chooser steps aside after `autoHideMs` unless touched.
export const CategoryChooser = ({ tags, selectedTags, onPick, onPlayAll, onLibrary, onShare, shareCopied, starting, layout = 'modal', filmCount = 0, testPrefix = 'film', onClose, onInteract, autoHideMs = 0 }) => {
  const sheet = layout === 'sheet';
  const interact = onInteract ? { onPointerDown: onInteract, onPointerMove: onInteract, onTouchStart: onInteract, onFocus: onInteract } : {};
  const body = (
    <div className={`relative ${sheet ? 'px-5 pt-6 pb-8' : 'p-8 md:p-10'}`} {...interact} data-autohide={autoHideMs ? '1' : '0'}>
      {autoHideMs > 0 && (
        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden" aria-hidden="true" data-testid={`${testPrefix}-chooser-autohide`}>
          <div className="h-full th-drain" style={{ background: 'var(--th-accent)', animationDuration: `${autoHideMs}ms` }} />
        </div>
      )}
      {onClose && (
        <button type="button" onClick={onClose} className="th-icon-btn absolute top-3 right-3 z-10" aria-label="Back to the film" data-testid={`${testPrefix}-chooser-close`}><X size={20} /></button>
      )}
      <div className={`${sheet ? '' : 'max-w-md'} th-rise`}>
        <Label>Curate your experience</Label>
        <h2 className={`th-display font-light mt-2 ${sheet ? 'text-3xl' : 'text-4xl md:text-5xl'}`} style={{ color: 'var(--th-text)' }} data-testid={`${testPrefix}-chooser-heading`}>
          Choose a celebration
        </h2>
        <p className="th-caption mt-3">{autoHideMs ? 'Tap one to begin, or sit back — the film continues on its own.' : 'Tap one to begin. Every film plays with sound.'}</p>
      </div>
      <hr className="th-rule th-draw my-6" />
      <div className={`flex flex-wrap ${sheet ? 'gap-2.5' : 'gap-3'}`} data-testid={`${testPrefix}-chooser-tags`}>
        {tags.map((tag, i) => {
          const active = selectedTags.includes(tag.name);
          return (
            <span key={tag.id} className={`th-frame th-cut th-cut-sm inline-flex th-rise ${active ? 'is-active' : ''}`} style={{ animationDelay: `${80 + i * 35}ms` }}>
              <button type="button" onClick={() => onPick(tag.name)} disabled={starting} className="th-cut th-cut-sm th-solid th-chip"
                data-testid={`${testPrefix}-tag-${tag.name}`} aria-pressed={active}>
                <span className="th-chip-dot" />
                {tag.display_name}
              </button>
            </span>
          );
        })}
      </div>
      <div className={`mt-8 flex flex-col gap-3 ${sheet ? '' : 'sm:flex-row sm:items-stretch'}`}>
        <ThButton variant="primary" onClick={onPlayAll} disabled={starting} className={sheet ? '' : 'sm:flex-1'} data-testid={`${testPrefix}-play-selected-btn`}>
          {starting ? <><Loader2 size={16} className="animate-spin" /> Starting</> : (selectedTags.length ? 'Play selected' : 'Play all films')}
        </ThButton>
        <ThButton variant="ghost" onClick={onLibrary} className={sheet ? '' : 'sm:flex-1'} data-testid={`${testPrefix}-view-all-btn`}>
          Film library{filmCount ? ` · ${filmCount}` : ''}
        </ThButton>
      </div>
      <ThButton variant="text" onClick={onShare} className="mt-2 text-[11px]" data-testid={`${testPrefix}-share-btn`}>
        {shareCopied ? <Check size={14} /> : <Share2 size={14} />}
        {shareCopied ? 'Link copied' : (selectedTags.length ? 'Share this filter' : 'Share the theatre')}
      </ThButton>
    </div>
  );

  if (sheet) return <div data-testid="film-chooser-sheet">{body}</div>;
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center px-4 th-fade pointer-events-none" data-testid="film-chooser-modal">
      <Cut size="lg" className="w-full max-w-2xl pointer-events-auto">{body}</Cut>
    </div>
  );
};
